// Libraries
import React from 'react';
import {css} from 'aphrodite/no-important';
// Core
import {randomElementFromArray} from '../core/Utilities';
import CoreMetrics, {FormFactorChangeListener, sharedFormFactorChangeListener, createExpandableMediaQueryStylesheet} from '../core/Metrics';
import ContentData from '../core/ContentData';
import {TitleText, HeadingLargeText, HeadingMediumText, HeadingSmallText, BodyText, MathText, CodeText, CaptionText} from '../core/Type';
import {FixedSpace} from '../core/Spacing';
// Layouts
import {Grid, GridRow, GridColumn, DockedTOCandContentLayout} from '../layouts/Grid';
import {CssGrid, CssGridRow, CssGridColumn, CssGridSpace} from '../layouts/CssGrid';
import MainAndSidebarLayout from '../layouts/MainAndSidebarLayout';
// Modules
import SiteBar from '../modules/SiteBar';
import LessonTOC from '../modules/LessonTOC';
import ShareBar from '../modules/ShareBar';
import SiteFooter from '../modules/SiteFooter';
// Images
import articleImage1 from './images/article-image-1.jpg';
import articleImage2 from './images/article-image-2.jpg';

export default class ArticlePage extends React.Component {
    render() {
        const styles = createExpandableMediaQueryStylesheet({
            page: {
                display: "flex",
                flexDirection: "column-reverse",
            },
            sidebar: {
                flexShrink: 0,
                marginRight: CoreMetrics.Grid.HorizontalGutter,
                width: CoreMetrics.Grid.Width.OneFourth,
                [CoreMetrics.MediaQuery().Desktop]: {
                    display: "block",
                },
                [CoreMetrics.MediaQuery().Tablet]: {
                    display: "none",
                },
                [CoreMetrics.MediaQuery().Phone]: {
                    display: "none",
                },
            },
            contentColumn: {
                display: "flex",
                flexDirection: "column",
            },
        });
        let page = null;
        const sidebarBehavior = sharedFormFactorChangeListener.getSidebarBehavior();
        if (sidebarBehavior === FormFactorChangeListener.SidebarBehavior.Docked) {
            page = (
                <div className={css(styles.page)}>
                    <DockedTOCandContentLayout toc={(
                        <LessonTOC variant={LessonTOC.Variant.Docked} />
                    )} content={(
                        <div className={css(styles.contentColumn)}>
                            <Grid>
                                <GridRow>
                                    <GridColumn variant={GridColumn.Variant.OneSixth}></GridColumn>
                                    <GridColumn>
                                        <ArticleHeader />
                                        <Article />
                                    </GridColumn>
                                    <GridColumn variant={GridColumn.Variant.OneSixth}></GridColumn>
                                </GridRow>
                            </Grid>
                            <FixedSpace variant={FixedSpace.Variant.Vertical} size={FixedSpace.Size.XLarge} />
                            <SiteFooter />
                        </div>
                    )} />
                    <SiteBar variant={SiteBar.Variant.Stretched} />
                </div>
            );
        } else {
            const sideSpace = <CssGridSpace
                size={0}
                sizeDesktop={1}
                sizeTablet={2}
            />;

            const mainContent = <CssGrid
                sizeTablet={12}
                sizeDesktop={9}
            >
                <CssGridRow>
                    {sideSpace}
                    <CssGridColumn
                        sizeTablet={8}
                        sizeDesktop={7}
                    >
                        <div className={css(styles.contentColumn)}>
                            <ArticleHeader />
                            <Article />
                        </div>
                    </CssGridColumn>
                    {sideSpace}
                </CssGridRow>
            </CssGrid>;

            page = (
                <div className={css(styles.page)}>
                    <SiteFooter />
                    <MainAndSidebarLayout
                        sidebarContent={<LessonTOC />}
                        mainContent={mainContent}
                    />
                    <SiteBar />
                </div>
            );
        }
        return page;
    }
}

class ArticleHeader extends React.Component {
    render() {
        const styles = createExpandableMediaQueryStylesheet({
            container: {
                paddingTop: CoreMetrics.ElementPadding.XLarge(),
            },
            about: {
                display: "flex",
                flexDirection: "column",
                paddingTop: CoreMetrics.ElementPadding.Medium(),
                paddingBottom: CoreMetrics.ElementPadding.Medium(),
            },
        });
        return (
            <div className={css(styles.container)}>
                <TitleText>{randomElementFromArray(ContentData.Content)}</TitleText>
                <div className={css(styles.about)}>
                    <BodyText>Velocity or speed? Instantaneous or average? Keep building your physics vocabulary.</BodyText>
                </div>
                <ShareBar />
            </div>
        );
    }
}

class Article extends React.Component {
    render() {
        const styles = createExpandableMediaQueryStylesheet({
            container: {
                paddingTop: CoreMetrics.ElementPadding.Medium(),
                paddingBottom: CoreMetrics.ElementPadding.XLarge(),
            },
            image: {
                maxWidth: "100%",
            },
        });
        return (
            <div className={css(styles.container)}>
                <ContentHeadingLarge>What does velocity mean?</ContentHeadingLarge>
                <FixedSpace variant={FixedSpace.Variant.Vertical} />
                <BodyText>Your notion of velocity is probably similar to its scientific definition. You know that a large displacement in a small amount of time means a large velocity and that velocity has units of distance divided by time, such as miles per hour or kilometers per hour.</BodyText>
                <FixedSpace variant={FixedSpace.Variant.Vertical} />
                <BodyText><StrongBodyText>Average velocity</StrongBodyText> is defined to be the change in position divided by the time of travel.</BodyText>
                <FixedSpace variant={FixedSpace.Variant.Vertical} />
                <MathText variant={MathText.Variant.Block} latex="v_{avg}=\dfrac{\Delta x}{\Delta t}=\dfrac{x_f-x_0}{t_f-t_0}" />
                <FixedSpace size={FixedSpace.Size.XLarge} variant={FixedSpace.Variant.Vertical} />
                <img className={css(styles.image)} src={articleImage1} alt="" />
                <CaptionText>Figure 1: A more detailed record of an airplane passenger heading toward the back of the plane, showing smaller segments of his trip. Image credit: Openstax College Physics</CaptionText>
                <FixedSpace size={FixedSpace.Size.XLarge} variant={FixedSpace.Variant.Vertical} />
                <BodyText>In this formula, <MathText latex="v_{avg}" /> is the average velocity; <MathText latex="\Delta x" /> is the change in position, or displacement; and <MathText latex="x_f" /> and <MathText latex="x_0" /> are the final and beginning positions at times <MathText latex="t_f" /> and <MathText latex="t_0" />, respectively. If the starting time <MathText latex="t_0" /> is taken to be zero, then the average velocity is written as below:</BodyText>
                <FixedSpace variant={FixedSpace.Variant.Vertical} />
                <MathText variant={MathText.Variant.Block} latex="v_{avg}=\dfrac{\Delta x}{t}" />
                <FixedSpace size={FixedSpace.Size.XLarge} variant={FixedSpace.Variant.Vertical} />
                <ContentHeadingLarge>What does speed mean?</ContentHeadingLarge>
                <FixedSpace variant={FixedSpace.Variant.Vertical} />
                <BodyText><StrongBodyText>Instantaneous speed</StrongBodyText> is the magnitude of instantaneous velocity. For example, suppose the airplane passenger at one instant had an instantaneous velocity of <MathText latex="-3.0 \dfrac{\text m}{\text{s}}" />, the negative meaning toward the rear of the plane. At that same time his instantaneous speed was <MathText latex="3.0 \dfrac{\text {m}}{\text{s}}" />. Or suppose that at a particular instant during a shopping trip, your instantaneous velocity is <MathText latex="40 \dfrac{\text {km}}{\text{hr}}" /> due north. Your instantaneous speed at that instant would be <MathText latex="40 \dfrac{\text {km}}{\text{hr}}" />, the same magnitude but without a direction. Average speed, however, is very different from average velocity. <StrongBodyText>Average speed</StrongBodyText> is the distance traveled divided by elapsed time. So, while the magnitudes of the instantaneous speed and velocity are always identical, the magnitudes of average speed and velocity can be very different.</BodyText>
                <FixedSpace size={FixedSpace.Size.XLarge} variant={FixedSpace.Variant.Vertical} />
                <img className={css(styles.image)} src={articleImage2}  alt="" />
                <CaptionText>Figure 2: During a 30-minute round trip to the store, the total distance traveled is 6 km. The average speed is 12 km/h. The displacement for the round trip is zero, since there was no net change in position. Thus the average velocity is zero. Image credit: Openstax College Physics</CaptionText>
                <FixedSpace size={FixedSpace.Size.XLarge} variant={FixedSpace.Variant.Vertical} />
                <ContentHeadingLarge>What do solved examples involving velocity and speed look like?</ContentHeadingLarge>
                <FixedSpace variant={FixedSpace.Variant.Vertical} />
                <ContentHeadingMedium>Example 1: Disoriented iguana</ContentHeadingMedium>
                <FixedSpace variant={FixedSpace.Variant.Vertical} />
                <BodyText>An iguana with a poor sense of spatial awareness is walking back and forth in the desert. First the iguana walks 12 meters to the right in a time of 20 seconds. Then the iguana runs 16 meters to the left in a time of 8 seconds.</BodyText>
                <FixedSpace variant={FixedSpace.Variant.Vertical} />
                <BodyText><StrongBodyText>What was the average speed and average velocity of the iguana for the entire trip?</StrongBodyText></BodyText>
                <FixedSpace variant={FixedSpace.Variant.Vertical} />
                <ContentHeadingSmall>Step 1: Start with the correct assumptions</ContentHeadingSmall>
                <FixedSpace variant={FixedSpace.Variant.Vertical} />
                <BodyText><EmphasizedBodyText>Assume that rightward is the positive direction.</EmphasizedBodyText></BodyText>
                <FixedSpace variant={FixedSpace.Variant.Vertical} />
                <UnorderedList>
                    <ListItem>To find the <EmphasizedBodyText>average speed</EmphasizedBodyText> we take the total distance traveled divided by the time interval.</ListItem>
                    <ListItem>To find the <EmphasizedBodyText>average velocity</EmphasizedBodyText> we take the displacement <MathText latex="\Delta x" /> divided by the time interval.</ListItem>
                </UnorderedList>
                <FixedSpace variant={FixedSpace.Variant.Vertical} />
                <ContentHeadingSmall>Step 2: Break down the problem</ContentHeadingSmall>
                <FixedSpace variant={FixedSpace.Variant.Vertical} />
                <BodyText>In order to solve this problem, we'll need to solve the following sub-problems:</BodyText>
                <FixedSpace variant={FixedSpace.Variant.Vertical} />
                <OrderedList>
                    <ListItem><EmphasizedBodyText>Average velocity</EmphasizedBodyText> is defined to be the displacement per time.</ListItem>
                    <ListItem><EmphasizedBodyText>Average speed</EmphasizedBodyText> is defined to be the distance traveled per time. The distance is the sum of the total path length traveled by the dolphin, so we just add up all the distances traveled by the dolphin for each leg of the trip.</ListItem>
                    <ListItem><EmphasizedBodyText>Instantaneous velocity</EmphasizedBodyText> is the velocity at a given moment and will be equal to the slope of the graph at that moment.</ListItem>
                </OrderedList>
                <FixedSpace size={FixedSpace.Size.XLarge} variant={FixedSpace.Variant.Vertical} />
                <ContentHeadingLarge>Code typeface testing</ContentHeadingLarge>
                <FixedSpace variant={FixedSpace.Variant.Vertical} />
                <BodyText>This sentence has some <CodeText>printf("Hello, world!");</CodeText> embedded in it.</BodyText>
                <FixedSpace variant={FixedSpace.Variant.Vertical} />
                <CodeText variant={CodeText.Variant.Block}>{"for (i = 0; i < 10; i++):\nprint(\"Hello, world!\")"}</CodeText>
            </div>
        );
    }
}

export class ContentHeadingLarge extends React.Component {
    render() {
        const styles = createExpandableMediaQueryStylesheet({
            headingLarge: {
                paddingTop: CoreMetrics.ElementSpacing.XSmall(),
            },
        });
        let combinedStyles = [styles.headingLarge, ...this.props.styles];
        return (
            <HeadingLargeText styles={combinedStyles}>
                {this.props.children}
            </HeadingLargeText>
        );
    }
}
ContentHeadingLarge.defaultProps = {
    styles: [],
};

export class ContentHeadingMedium extends React.Component {
    render() {
        const styles = createExpandableMediaQueryStylesheet({
            headingMedium: {
                paddingTop: CoreMetrics.ElementSpacing.XSmall(),
            },
        });
        let combinedStyles = [styles.headingMedium, ...this.props.styles];
        return (
            <HeadingMediumText styles={combinedStyles}>
                {this.props.children}
            </HeadingMediumText>
        );
    }
}
ContentHeadingMedium.defaultProps = {
    styles: [],
};

export class ContentHeadingSmall extends React.Component {
    render() {
        const styles = createExpandableMediaQueryStylesheet({
            headingSmall: {
                paddingTop: CoreMetrics.ElementSpacing.XSmall(),
            },
        });
        let combinedStyles = [styles.headingSmall, ...this.props.styles];
        return (
            <HeadingSmallText styles={combinedStyles}>
                {this.props.children}
            </HeadingSmallText>
        );
    }
}
ContentHeadingSmall.defaultProps = {
    styles: [],
};

export class StrongBodyText extends React.Component {
    render() {
        const styles = createExpandableMediaQueryStylesheet({
            strong: {
                fontWeight: 400,
                fontFamily: CoreMetrics.Type.Font.SansSerifMedium,
            },
        });
        let combinedStyles = [styles.strong, ...this.props.styles];
        return (
            <BodyText
                tag="span"
                styles={combinedStyles}
            >
                {this.props.children}
            </BodyText>
        );
    }
}
StrongBodyText.defaultProps = {
    styles: [],
};

export class EmphasizedBodyText extends React.Component {
    render() {
        const styles = createExpandableMediaQueryStylesheet({
            em: {
                fontStyle: "italic",
            },
        });
        let combinedStyles = [styles.em, ...this.props.styles];
        return (
            <BodyText
                tag="span"
                styles={combinedStyles}
            >
                {this.props.children}
            </BodyText>
        );
    }
}
EmphasizedBodyText.defaultProps = {
    styles: [],
};

export class OrderedList extends React.Component {
    render() {
        const styles = createExpandableMediaQueryStylesheet({
            orderedList: {
                listStyleType: "decimal",
                listStylePosition: "outside",
                margin: 0,
                padding: 0,
                paddingLeft: CoreMetrics.ElementPadding.XLarge(),
            },
        });
        let combinedStyles = [styles.orderedList, ...this.props.styles];
        return (
            <ol className={css(combinedStyles)}>
                {this.props.children}
            </ol>
        );
    }
}
OrderedList.defaultProps = {
    styles: [],
};

export class UnorderedList extends React.Component {
    render() {
        const styles = createExpandableMediaQueryStylesheet({
            unorderedList: {
                listStyleType: "disc",
                listStylePosition: "outside",
                margin: 0,
                padding: 0,
                paddingLeft: CoreMetrics.ElementPadding.XLarge(),
            },
        });
        let combinedStyles = [styles.unorderedList, ...this.props.styles];
        return (
            <ul className={css(combinedStyles)}>
                {this.props.children}
            </ul>
        );
    }
}
UnorderedList.defaultProps = {
    styles: [],
};

export class ListItem extends React.Component {
    render() {
        const styles = createExpandableMediaQueryStylesheet({
            listItem: {
                paddingLeft: CoreMetrics.ElementPadding.XXSmall(),
                marginBottom: CoreMetrics.ElementPadding.XSmall(),
                ":last-child": {
                    marginBottom: 0,
                }
            },
        });
        let combinedStyles = [styles.listItem, ...this.props.styles];
        return (
            <BodyText
                tag="li"
                styles={combinedStyles}
            >
                {this.props.children}
            </BodyText>
        );
    }
}
ListItem.defaultProps = {
    styles: [],
};



// WEBPACK FOOTER //
// ./src/demo/ArticlePage.jsx